import React from "react";
import "./nav-menu-item.scss";

export interface NavMenuItemProps {
  label: string;
  active?: boolean;
  /** Color of the left accent border and active text. Defaults to #0056D6. */
  accentColor?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Content rendered before the label. */
  prefix?: React.ReactNode;
  /** Content rendered after the label, pushed to the right end. */
  suffix?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  /** Background color when the item is not active. */
  backgroundColor?: string;
  /** Additional CSS class applied to the label when the item is active. */
  activeLabelClassName?: string;
  /** Additional CSS class applied to the label when the item is not active. */
  inactiveLabelClassName?: string;
  /** Whether to truncate the label text. */
  shouldTruncate?: boolean;
}

export function NavMenuItem({
  label,
  active = false,
  accentColor,
  onClick,
  prefix,
  suffix,
  disabled = false,
  className = "",
  backgroundColor,
  activeLabelClassName,
  inactiveLabelClassName,
  shouldTruncate = false,
}: NavMenuItemProps) {
  const itemClass = [
    "nav-menu-item",
    active && "nav-menu-item--active",
    disabled && "nav-menu-item--disabled",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const labelClass = [
    "nav-menu-item__label",
    active ? activeLabelClassName : inactiveLabelClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const itemStyle: React.CSSProperties = {
    ...(active && accentColor
      ? { borderLeftColor: accentColor, color: accentColor }
      : undefined),
    ...(!active && backgroundColor
      ? { backgroundColor }
      : undefined),
  };

  const hasStyle = Object.keys(itemStyle).length > 0;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    onClick?.(e);
  };

  return (
    <div
      className={itemClass}
      style={hasStyle ? itemStyle : undefined}
      onClick={handleClick}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-current={active ? "page" : undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }}
    >
      {prefix && <span className="nav-menu-item__prefix">{prefix}</span>}
      <span className={`${labelClass} ${shouldTruncate ? "nav-menu-item__label--truncate" : ""}`} title={shouldTruncate ? label : undefined}>
        {label}
      </span>
      {suffix && <span className="nav-menu-item__suffix">{suffix}</span>}
    </div>
  );
}
